/**
 * Geometry for the small coordinate-plane graphs: windows, scales and tick marks.
 *
 * Pure numbers in, numbers out, so the graph components only draw.
 */

export interface Viewport {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

/** A step of 1, 2 or 5 × 10ᵏ that cuts `span` into about `target` pieces. */
export function niceStep(span: number, target = 6): number {
  if (!(span > 0 && Number.isFinite(span))) {
    return 1;
  }
  const raw = span / target;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / magnitude;
  if (fraction <= 1) {
    return magnitude;
  }
  if (fraction <= 2) {
    return 2 * magnitude;
  }
  if (fraction <= 5) {
    return 5 * magnitude;
  }
  return 10 * magnitude;
}

/** Multiples of `step` inside [min, max]. */
export function ticksBetween(min: number, max: number, step: number): number[] {
  const ticks: number[] = [];
  const first = Math.ceil(min / step);
  const last = Math.floor(max / step);
  for (let k = first; k <= last && ticks.length < 50; k += 1) {
    // Rounded so 0.30000000000000004 is labelled 0.3.
    ticks.push(Number((k * step).toPrecision(12)));
  }
  return ticks;
}

/** Decimal places a tick label needs at this step: 0.25 → 2, 5 → 0. */
export function tickDecimals(step: number): number {
  return Math.max(0, -Math.floor(Math.log10(step) + 1e-9));
}

/** [min, max] padded by a fraction of the span, never narrower than `minSpan`. */
export function padRange(
  min: number,
  max: number,
  fraction: number,
  minSpan: number
): [number, number] {
  const span = max - min;
  if (span < minSpan) {
    const middle = (min + max) / 2;
    return [middle - minSpan / 2, middle + minSpan / 2];
  }
  return [min - span * fraction, max + span * fraction];
}

/** A linear map from data to pixels. */
export function linearScale(
  domainMin: number,
  domainMax: number,
  rangeMin: number,
  rangeMax: number
): (value: number) => number {
  const ratio = (rangeMax - rangeMin) / (domainMax - domainMin || 1);
  return (value) => rangeMin + (value - domainMin) * ratio;
}

/** An SVG path through f sampled across the viewport, broken where f leaves it. */
export function samplePath(
  f: (x: number) => number,
  viewport: Viewport,
  toX: (x: number) => number,
  toY: (y: number) => number,
  samples = 200
): string {
  const commands: string[] = [];
  const span = viewport.xMax - viewport.xMin;
  const limit = (viewport.yMax - viewport.yMin) * 4;
  let penDown = false;
  for (let i = 0; i <= samples; i += 1) {
    const x = viewport.xMin + (span * i) / samples;
    const y = f(x);
    const visible =
      Number.isFinite(y) &&
      y > viewport.yMin - limit &&
      y < viewport.yMax + limit;
    if (!visible) {
      penDown = false;
      continue;
    }
    commands.push(
      `${penDown ? "L" : "M"}${toX(x).toFixed(2)} ${toY(y).toFixed(2)}`
    );
    penDown = true;
  }
  return commands.join(" ");
}

/**
 * Widen whichever axis is denser so one unit is the same length on both — a line of
 * slope 1 then really leaves at 45°, which matters when the angle is the answer.
 */
export function equalizeAspect(
  viewport: Viewport,
  plotWidth: number,
  plotHeight: number
): Viewport {
  const xSpan = viewport.xMax - viewport.xMin;
  const ySpan = viewport.yMax - viewport.yMin;
  const xDensity = plotWidth / xSpan;
  const yDensity = plotHeight / ySpan;
  if (xDensity > yDensity) {
    const grow = (plotWidth / yDensity - xSpan) / 2;
    return {
      ...viewport,
      xMin: viewport.xMin - grow,
      xMax: viewport.xMax + grow,
    };
  }
  const grow = (plotHeight / xDensity - ySpan) / 2;
  return {
    ...viewport,
    yMin: viewport.yMin - grow,
    yMax: viewport.yMax + grow,
  };
}
